import { useState, useCallback, useRef, useEffect } from 'react';
import type { ModalType, PatientActionType, PatientHandlers } from '../types/PatientScreenTypes';
import { shouldPromptBeforeAction } from '../delayReasonUtils';

export interface PatientScreenContext {
  patient: any;
  room: any;
  showDelayReasons: boolean;
}

/**
 * Main hook for PatientScreen state management
 *
 * CRITICAL: Includes memory leak fix using isMountedRef
 * This prevents "Can't perform a React state update on an unmounted component" warnings
 */
export function usePatientScreen(handlers: PatientHandlers, context?: PatientScreenContext) {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const isMountedRef = useRef(true);

  // Memory leak fix: Track if component is mounted
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Safe state setter - only updates if component is still mounted
  const safeSetActiveModal = useCallback((modal: ModalType) => {
    if (isMountedRef.current) {
      setActiveModal(modal);
    }
  }, []);

  // Handle all patient actions
  const handleAction = useCallback(
    async (action: PatientActionType) => {
      // Actions that open modals
      switch (action) {
        case 'assignBed':
          safeSetActiveModal('bedPicker');
          return;

        case 'assignPriority':
        case 'preOpPriority':
          safeSetActiveModal('priorityPicker');
          return;

        case 'procedurePriority':
          safeSetActiveModal('procedurePriorityPicker');
          return;

        case 'anesthesiaEnd':
          safeSetActiveModal('anesthesiaEnd');
          return;

        case 'moveToOr':
          safeSetActiveModal('moveToOR');
          return;

        case 'moveToPostOp':
          safeSetActiveModal('moveToPostOp');
          return;

        case 'dischargePatient':
          safeSetActiveModal('dischargeConfirm');
          return;

        case 'cancelVisit':
          safeSetActiveModal('cancelVisitConfirm');
          return;
      }

      // Actions that open confirmation modals with additional info (transfer modals show duration)
      // These are handled in PatientScreen.tsx now with duration display
      if (action === 'transferToChair') {
        safeSetActiveModal('transferToChair');
        return;
      }

      if (action === 'transferToPT') {
        safeSetActiveModal('transferToPT');
        return;
      }

      if (action === 'ptComplete') {
        safeSetActiveModal('ptComplete');
        return;
      }

      // Actions that execute immediately (or check for delay)
      try {
        // Check for delays if context is provided
        if (context && context.showDelayReasons) {
          const { patient, room, showDelayReasons } = context;

          if (action === 'dischargePhysicianLastPatient') {
            if (shouldPromptBeforeAction({ patient, room, delayType: 'pacuOut', showDelayReasons })) {
              safeSetActiveModal('dischargePatientPhysicianLastDelayReason');
              return;
            }
          }

          if (action === 'dischargeAnesthesiologistLastPatient') {
            if (shouldPromptBeforeAction({ patient, room, delayType: 'pacuOut', showDelayReasons })) {
              safeSetActiveModal('dischargePatientAnesthesiologistLastDelayReason');
              return;
            }
          }

          if (action === 'dischargeBothLastPatient') {
            if (shouldPromptBeforeAction({ patient, room, delayType: 'pacuOut', showDelayReasons })) {
              safeSetActiveModal('dischargePatientBothLastDelayReason');
              return;
            }
          }
        }

        switch (action) {
          case 'markAsReady':
            await handlers.onMarkAsReady();
            break;

          case 'markAsReadyConfirm':
            // Show confirmation modal for "Ready to See Family" in PACU/PostOp
            safeSetActiveModal('readyForFamilyConfirm');
            return;

          case 'markAsNotReady':
            await handlers.onMarkAsNotReady();
            break;

          case 'markAsReadyForSurgeon':
            await handlers.onMarkAsReadyForSurgeon();
            break;

          case 'markAsNotReadyForSurgeon':
            await handlers.onMarkAsNotReadyForSurgeon();
            break;

          case 'markAsReadyForAnesthesia':
            await handlers.onMarkAsReadyForAnesthesia();
            break;

          case 'markAsNotReadyForAnesthesia':
            await handlers.onMarkAsNotReadyForAnesthesia();
            break;

          case 'markAsSpinalTimeout':
            await handlers.onMarkAsSpinalTimeout();
            break;

          case 'markAsUndoSpinalTimeout':
            await handlers.onMarkAsUndoSpinalTimeout();
            break;

          case 'holdProcedure':
            await handlers.onHoldProcedure();
            break;

          case 'undoHoldProcedure':
            await handlers.onUndoHoldProcedure();
            break;

          case 'blockNerve':
            await handlers.onBlockNerve();
            break;

          case 'undoBlockNerve':
            await handlers.onUndoBlockNerve();
            break;

          case 'markAsReadyToSeeFamilyPreOp':
            await handlers.onMarkAsReadyToSeeFamilyPreOp();
            break;

          case 'markAsNotReadyToSeeFamilyPreOp':
            await handlers.onMarkAsNotReadyToSeeFamilyPreOp();
            break;

          case 'markAsFamilyReady':
            // Show confirmation modal for "Ready for Pickup"
            safeSetActiveModal('readyForPickupConfirm');
            return;

          case 'dischargePhysicianLastPatient':
            await handlers.onDischargePhysicianLastPatient();
            break;

          case 'dischargeAnesthesiologistLastPatient':
            await handlers.onDischargeAnesthesiologistLastPatient();
            break;

          case 'dischargeBothLastPatient':
            await handlers.onDischargeBothLastPatient();
            break;

          // Note: transferToChair, transferToPT, ptComplete are now handled via modals
          // in PatientScreen.tsx that show duration info before confirmation

          case 'editTransferToChair':
            safeSetActiveModal('editTransferToChair');
            return;

          case 'editTransferToPT':
            safeSetActiveModal('editTransferToPT');
            return;

          case 'editPTComplete':
            safeSetActiveModal('editPTComplete');
            return;
        }

        // Navigate back to patient list after action completes
        // Patient list will show updated status badges via subscription
        if (isMountedRef.current) {
          handlers.goBack();
        }
      } catch (error) {
        console.error(`Error executing action ${action}:`, error);
      }
    },
    [handlers, safeSetActiveModal, context]
  );

  // Close modal handler
  const closeModal = useCallback(() => {
    safeSetActiveModal(null);
  }, [safeSetActiveModal]);

  return {
    activeModal,
    setActiveModal: safeSetActiveModal,
    closeModal,
    handleAction,
    isMounted: () => isMountedRef.current,
  };
}
